import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { 
  FiX, FiMessageCircle, FiPhone, FiVideo, FiUserPlus, 
  FiMicOff, FiVideoOff, FiPhoneOff, FiSend 
} = FiIcons;

const MemberProfileModal = ({ member, onClose }) => {
  const [activeView, setActiveView] = useState('profile'); // 'profile', 'chat', 'call', 'video'
  const [chatMessage, setChatMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([
    { sender: 'them', text: `Hi, I'm here to listen if you need someone to talk to.` }
  ]);

  if (!member) return null;

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!chatMessage.trim()) return;
    
    setChatHistory([...chatHistory, { sender: 'me', text: chatMessage }]);
    setChatMessage('');
    
    // Simulate a reply
    setTimeout(() => {
      setChatHistory(prev => [...prev, { sender: 'them', text: 'Thank you for reaching out. We are in this together.' }]);
    }, 1500);
  };

  const handleClose = () => {
    if (activeView === 'profile') {
      onClose();
    } else {
      setActiveView('profile');
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-accent-navy/60 backdrop-blur-sm"
    >
      <motion.div 
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        className="bg-white rounded-[40px] shadow-2xl w-full max-w-sm overflow-hidden border border-brand-dark relative"
      >
        <div className="relative h-32 bg-accent-gold/20 transition-all duration-300">
          <button 
            onClick={handleClose} 
            className="absolute top-4 right-4 p-3 bg-white/40 hover:bg-white/80 rounded-full text-accent-navy transition-all z-10 backdrop-blur-md"
          >
            <SafeIcon icon={FiX} />
          </button>
        </div>
        
        <AnimatePresence mode="wait">
          {activeView === 'profile' && (
            <motion.div 
              key="profile"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.2 }}
              className="px-8 pb-10 -mt-16 text-center"
            >
              <div className="relative inline-block mb-4">
                <img src={member.avatar} className="w-32 h-32 rounded-[40px] border-4 border-white shadow-xl object-cover" alt="" />
                <div className={`absolute bottom-2 right-2 w-6 h-6 rounded-full border-4 border-white ${member.status === 'online' ? 'bg-green-500' : 'bg-gray-400'}`} />
              </div>

              <h3 className="text-2xl font-serif text-accent-navy mb-1">{member.name}</h3>
              <p className="text-[10px] font-black uppercase tracking-widest text-accent-gold mb-6">{member.relation} Sanctuary Member</p>
              
              <p className="text-sm text-text-muted font-light leading-relaxed mb-8 italic">
                "{member.bio}"
              </p>

              <div className="grid grid-cols-3 gap-3 mb-8">
                <SocialAction 
                  icon={FiMessageCircle} 
                  label="Chat" 
                  color="text-blue-500" 
                  bg="bg-blue-50 hover:bg-blue-100" 
                  onClick={() => setActiveView('chat')} 
                />
                <SocialAction 
                  icon={FiPhone} 
                  label="Call" 
                  color="text-green-500" 
                  bg="bg-green-50 hover:bg-green-100" 
                  onClick={() => setActiveView('call')} 
                />
                <SocialAction 
                  icon={FiVideo} 
                  label="Video" 
                  color="text-purple-500" 
                  bg="bg-purple-50 hover:bg-purple-100" 
                  onClick={() => setActiveView('video')} 
                />
              </div>

              <button className="w-full btn-primary py-4 text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2">
                <SafeIcon icon={FiUserPlus} /> Connect with {member.name.split(' ')[0]}
              </button>
            </motion.div>
          )}

          {activeView === 'chat' && (
            <motion.div
              key="chat"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.2 }}
              className="px-6 pb-6 -mt-16 h-[420px] flex flex-col"
            >
              <div className="flex items-center gap-4 mb-4 pt-4 border-b border-brand-dark/30 pb-4">
                <div className="relative">
                  <img src={member.avatar} className="w-12 h-12 rounded-full border-2 border-white shadow-md object-cover" alt="" />
                  <div className="absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white bg-green-500" />
                </div>
                <div className="text-left">
                  <h4 className="font-bold text-accent-navy">{member.name}</h4>
                  <p className="text-[10px] text-green-500 font-bold uppercase tracking-widest">Online</p>
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto mb-4 space-y-3 pr-2 flex flex-col pt-2">
                {chatHistory.map((msg, i) => (
                  <div key={i} className={`max-w-[85%] p-3 rounded-2xl text-sm ${msg.sender === 'me' ? 'bg-accent-navy text-white self-end rounded-br-sm' : 'bg-brand-light text-accent-navy self-start rounded-tl-sm border border-brand-dark'}`}>
                    {msg.text}
                  </div>
                ))}
              </div>

              <form onSubmit={handleSendMessage} className="flex gap-2 mt-auto">
                <input 
                  type="text" 
                  value={chatMessage}
                  onChange={(e) => setChatMessage(e.target.value)}
                  placeholder="Type a message..." 
                  className="flex-1 bg-brand-light border border-brand-dark rounded-full px-4 py-2 text-sm focus:outline-none focus:border-accent-gold"
                />
                <button 
                  type="submit" 
                  disabled={!chatMessage.trim()}
                  className="w-10 h-10 bg-accent-gold text-accent-navy rounded-full flex items-center justify-center hover:scale-105 disabled:opacity-50 transition-all flex-shrink-0"
                >
                  <SafeIcon icon={FiSend} className="w-4 h-4" />
                </button>
              </form>
            </motion.div>
          )}

          {activeView === 'call' && (
            <motion.div
              key="call"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.2 }}
              className="px-8 pb-10 -mt-16 text-center h-[420px] flex flex-col items-center justify-center"
            >
              <div className="relative inline-block mb-8 mt-8">
                <div className="absolute inset-0 bg-green-500/20 rounded-full animate-ping"></div>
                <img src={member.avatar} className="w-32 h-32 rounded-full border-4 border-green-500 shadow-xl object-cover relative z-10" alt="" />
              </div>
              <h3 className="text-2xl font-serif text-accent-navy mb-2">{member.name}</h3>
              <p className="text-sm text-text-muted mb-auto">Calling...</p>

              <div className="flex justify-center gap-6 mt-8">
                <button className="w-14 h-14 bg-brand-light text-accent-navy rounded-full flex items-center justify-center hover:bg-brand-dark transition-colors border border-brand-dark">
                  <SafeIcon icon={FiMicOff} className="w-6 h-6" />
                </button>
                <button onClick={() => setActiveView('profile')} className="w-14 h-14 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-lg shadow-red-500/30 hover:scale-105">
                  <SafeIcon icon={FiPhoneOff} className="w-6 h-6" />
                </button>
              </div>
            </motion.div>
          )}

          {activeView === 'video' && (
            <motion.div
              key="video"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.2 }}
              className="px-6 pb-8 -mt-24 text-center"
            >
              <div className="relative w-full h-72 bg-accent-navy rounded-[32px] overflow-hidden mb-6 shadow-inner border-4 border-white">
                <div className="absolute inset-0 flex flex-col items-center justify-center text-white/50 bg-black/40 backdrop-blur-sm">
                  <SafeIcon icon={FiVideo} className="w-12 h-12 mb-4 animate-pulse text-accent-gold" />
                  <p className="text-sm font-medium tracking-wide">Connecting securely...</p>
                </div>
                {/* Simulated self view */}
                <div className="absolute bottom-4 right-4 w-20 h-28 bg-gray-800 rounded-2xl border-2 border-white/20 overflow-hidden shadow-lg">
                   <div className="w-full h-full flex items-center justify-center text-white/30 bg-black/50">
                      <SafeIcon icon={FiVideoOff} className="w-6 h-6" />
                   </div>
                </div>
              </div>
              
              <h3 className="text-xl font-serif text-accent-navy mb-6">{member.name}</h3>

              <div className="flex justify-center gap-4">
                <button className="w-12 h-12 bg-brand-light text-accent-navy rounded-full flex items-center justify-center hover:bg-brand-dark transition-colors border border-brand-dark">
                  <SafeIcon icon={FiMicOff} className="w-5 h-5" />
                </button>
                <button className="w-12 h-12 bg-brand-light text-accent-navy rounded-full flex items-center justify-center hover:bg-brand-dark transition-colors border border-brand-dark">
                  <SafeIcon icon={FiVideoOff} className="w-5 h-5" />
                </button>
                <button onClick={() => setActiveView('profile')} className="w-12 h-12 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-lg shadow-red-500/30 hover:scale-105">
                  <SafeIcon icon={FiPhoneOff} className="w-5 h-5" />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

const SocialAction = ({ icon, label, color, bg, onClick }) => (
  <button 
    onClick={onClick} 
    className={`flex flex-col items-center gap-2 p-3 rounded-2xl transition-all duration-300 hover:scale-105 hover:-translate-y-1 ${bg} ${color}`}
  >
    <SafeIcon icon={icon} className="w-5 h-5" />
    <span className="text-[8px] font-black uppercase tracking-widest">{label}</span>
  </button>
);

export default MemberProfileModal;